/**
 * PhaseCards.jsx
 *
 * One row per phase: name, price, per-wallet cap, a single-word status,
 * and — once a wallet is connected — whether that wallet is eligible.
 *
 * Status is deliberately limited to LIVE / UPCOMING / ENDED. Anything
 * finer (CLOSED, PASSED, NOT SCHEDULED…) made three cards read like six
 * different states.
 *
 * One caveat on ENDED: `gtdMint` has no end time in the contract, so a
 * whitelisted wallet can technically still use its allocation after the
 * public phase opens. The label reflects the sale's intent, not a
 * contract restriction.
 */
import React from 'react';
import GlowText from '../GlowText';
import CountdownTimer from './CountdownTimer';
import { PHASE } from '../../hooks/useMintContract';

const STATUS = { LIVE: 'live', UPCOMING: 'upcoming', ENDED: 'ended' };

function StatusPill({ status }) {
  const label = { live: 'LIVE', upcoming: 'UPCOMING', ended: 'ENDED' }[status];
  return (
    <span className={`status-pill ${status}`}>
      {status === 'live' && (
        <span
          style={{
            width: 5, height: 5, borderRadius: '50%',
            background: '#05070c', display: 'inline-block',
          }}
        />
      )}
      {label}
    </span>
  );
}

/**
 * Verdicts are green or red and glow continuously, so a holder can read
 * their standing at a glance without parsing the surrounding text.
 * Anything that is not a clear yes reads as red rather than a neutral
 * grey — "maybe" is not a useful answer when a phase is about to open.
 */
function Eligibility({ state }) {
  const map = {
    eligible:      ['yes', '✓', 'ELIGIBLE'],
    'open-to-all': ['yes', '✓', 'ELIGIBLE'],
    owner:         ['yes', '★', 'TEAM WALLET'],
    used:          ['no',  '!', 'LIMIT REACHED'],
    'not-listed':  ['no',  '✕', 'NOT ELIGIBLE'],
    // A connected wallet must never be told to connect. These three are
    // separate states that previously all collapsed into "CONNECT TO
    // CHECK", which is simply false once a wallet is attached.
    checking:      ['neutral', '·', 'CHECKING…'],
    'check-failed':['no',  '!', 'CHECK FAILED'],
    unknown:       ['neutral', '·', 'CONNECT TO CHECK'],
  };
  const [tone, icon, label] = map[state] || map.unknown;

  if (tone === 'neutral') {
    return (
      <span className="flex items-center gap-1.5 shrink-0">
        <span
          className="flex items-center justify-center"
          style={{ width: 17, height: 17, borderRadius: 3, border: '1px solid var(--steel-light)', color: 'var(--text-dim)', fontSize: 9 }}
        >
          {icon}
        </span>
        <span className="font-mono-hud" style={{ fontSize: 11, color: 'var(--text-dim)' }}>{label}</span>
      </span>
    );
  }

  const yes = tone === 'yes';
  return (
    <span className="flex items-center gap-1.5 shrink-0">
      <span
        className={`flex items-center justify-center ${yes ? 'elig-box-yes' : 'elig-box-no'}`}
        style={{
          width: 17, height: 17, borderRadius: 3,
          border: '1px solid',
          color: yes ? 'var(--ok-green)' : 'var(--no-red)',
          fontSize: 10, fontWeight: 800,
        }}
      >
        {icon}
      </span>
      <span className={`font-display ${yes ? 'elig-yes' : 'elig-no'}`} style={{ fontSize: 11 }}>
        {label}
      </span>
    </span>
  );
}

function PhaseRow({ index, title, price, cap, status, detail, eligibility }) {
  const live = status === STATUS.LIVE;
  return (
    <div
      className={`mech-panel ${live ? 'phase-live' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '13px 15px',
        borderColor: live ? 'var(--cyan)' : 'var(--steel)',
        // Ended phases stay fully readable — the pill communicates the
        // state. Fading the text as well made real information (caps,
        // counts, your own eligibility) hard to read for no gain.
        opacity: 1,
      }}
    >
      {live && <span className="phase-scan" />}

      <div className="flex items-center gap-2.5 mb-2.5">
        <span
          className="flex items-center justify-center shrink-0 font-mono-hud"
          style={{
            width: 22, height: 22, borderRadius: 4,
            border: `1px solid ${live ? 'var(--cyan)' : 'var(--steel-light)'}`,
            color: live ? 'var(--cyan)' : 'var(--text-dim)',
            fontSize: 11,
          }}
        >
          {index}
        </span>
        <GlowText text={title} size={12} variant={live ? 'cyan' : 'white'} />
        <span style={{ marginLeft: 'auto' }}><StatusPill status={status} /></span>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ rowGap: 6 }}>
        <span className="font-mono-hud" style={{ fontSize: 12, color: 'var(--text)' }}>
          {price}
          <span className="text-dim"> · max </span>
          <span style={{ color: 'var(--violet)' }}>{cap === 0 || cap === '0' ? 'UNLIMITED' : cap}</span>
        </span>
        {detail}
      </div>

      <div
        className="flex items-center justify-between gap-2 flex-wrap"
        style={{ marginTop: 10, paddingTop: 9, borderTop: '1px solid var(--steel)', rowGap: 6 }}
      >
        <span className="font-mono-hud text-dim" style={{ fontSize: 10, letterSpacing: '0.1em' }}>
          YOUR STATUS
        </span>
        <Eligibility state={eligibility} />
      </div>
    </div>
  );
}

export default function PhaseCards({
  phase, gtdStartTime, publicStartTime, clockSkew, onTick,
  priceLabel, gtdCap, publicCap,
  connected, isOwner, whitelistStatus, gtdUsed, publicUsed,
  teamMinted = 0, teamReserve = 222,
}) {
  // ── Status ──
  // Team is mintable by the owner at any point, but the reserve is
  // finite — once all TEAM_RESERVE units are minted the phase is over,
  // so it should not keep advertising itself as live.
  const teamStatus = teamMinted >= teamReserve ? STATUS.ENDED : STATUS.LIVE;
  const gtdStatus =
    phase >= PHASE.PUBLIC ? STATUS.ENDED
    : phase === PHASE.GTD ? STATUS.LIVE
    : STATUS.UPCOMING;
  const publicStatus =
    phase === PHASE.SOLD_OUT ? STATUS.ENDED
    : phase === PHASE.PUBLIC ? STATUS.LIVE
    : STATUS.UPCOMING;

  // ── Eligibility ──
  const teamElig = !connected ? 'unknown' : isOwner ? 'owner' : 'not-listed';

  let gtdElig = 'unknown';
  if (connected) {
    if (whitelistStatus.error) gtdElig = 'check-failed';
    else if (whitelistStatus.loading || !whitelistStatus.checked) gtdElig = 'checking';
    else if (!whitelistStatus.isWhitelisted) gtdElig = 'not-listed';
    else gtdElig = (gtdCap !== 0 && gtdUsed >= gtdCap) ? 'used' : 'eligible';
  }

  // publicCap === 0 means unlimited (mirrors the contract). Without this
  // check, 0 >= 0 reads as "limit reached" for a wallet that has minted
  // nothing — which is the exact opposite of what unlimited means.
  const publicElig = !connected
    ? 'unknown'
    : (publicCap !== 0 && publicUsed >= publicCap) ? 'used' : 'open-to-all';

  const countdown = (target) => (
    <CountdownTimer target={target} onComplete={onTick} size={12} variant="violet" clockSkew={clockSkew} />
  );

  return (
    <div className="flex flex-col gap-2.5">
      <PhaseRow
        index={1} title="TEAM" price="FREE" cap={teamReserve}
        status={teamStatus}
        detail={<span className="font-mono-hud text-dim" style={{ fontSize: 11 }}>{teamMinted} / {teamReserve} minted</span>}
        eligibility={teamElig}
      />
      <PhaseRow
        index={2} title="GTD WHITELIST" price={priceLabel} cap={gtdCap}
        status={gtdStatus}
        detail={gtdStatus === STATUS.UPCOMING ? countdown(gtdStartTime) : null}
        eligibility={gtdElig}
      />
      <PhaseRow
        index={3} title="PUBLIC" price={priceLabel} cap={publicCap}
        status={publicStatus}
        detail={publicStatus === STATUS.UPCOMING ? countdown(publicStartTime) : null}
        eligibility={publicElig}
      />
    </div>
  );
}
